import React, { useEffect, useMemo, useState } from 'react'
import { Box, TextField, Button, InputAdornment, IconButton, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'

interface FixedFilter {
  key: string
  label?: string
  value: string | number
  removable?: boolean
}

interface SearchBarProps {
  // onSearch can accept a raw string or a structured payload with filters
  onSearch: (query: string | { q: string; filters?: Record<string, string | number> }) => void
  debounceMs?: number
  trigger?: 'enter' | 'button' | 'change'
  minLength?: number
  placeholderKey?: string
  fixedFilters?: FixedFilter[]
  showClear?: boolean
  loading?: boolean
}

const normalize = (s: string) =>
  s
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  debounceMs = 300,
  trigger = 'enter',
  minLength = 0,
  placeholderKey,
  fixedFilters = [],
  showClear = true,
  loading = false,
}) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilters, setActiveFilters] = useState<FixedFilter[]>(fixedFilters)

  useEffect(() => {
    setActiveFilters(fixedFilters)
  }, [fixedFilters])

  // Build filters map for payload
  const filtersMap = useMemo(() => {
    const obj: Record<string, string | number> = {}
    activeFilters.forEach((f) => (obj[f.key] = f.value))
    return obj
  }, [activeFilters])

  // Debounced change handler for trigger === 'change'
  useEffect(() => {
    if (trigger !== 'change') return
    const normalized = normalize(searchQuery)
    if (normalized.length < minLength && normalized.length !== 0) return

    const handler = setTimeout(() => {
      if (normalized.length === 0 && minLength > 0) {
        onSearch({ q: '', filters: filtersMap })
        return
      }
      onSearch({ q: normalized, filters: Object.keys(filtersMap).length ? filtersMap : undefined })
    }, debounceMs)

    return () => clearTimeout(handler)

  }, [searchQuery, debounceMs, trigger, minLength, filtersMap])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const performSearch = (forceNormalized?: string) => {
    const raw = forceNormalized !== undefined ? forceNormalized : searchQuery
    const normalized = normalize(raw)
    if (normalized.length < minLength && normalized.length !== 0) return

    // If there are filters, send an object payload
    if (Object.keys(filtersMap).length) {
      onSearch({ q: normalized, filters: filtersMap })
    } else {
      onSearch(normalized)
    }
  }

  const handleSearchButton = () => performSearch()

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && trigger !== 'change') {
      performSearch()
    }
  }

  const handleClear = () => {
    setSearchQuery('')
    if (trigger === 'change') {
      // clear will trigger debounced effect
      return
    }
    // immediate clear search
    if (Object.keys(filtersMap).length) {
      onSearch({ q: '', filters: filtersMap })
    } else {
      onSearch('')
    }
  }

  const handleRemoveFilter = (key: string) => {
    setActiveFilters((prev) => prev.filter((f) => f.key !== key))
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 5, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <TextField
          variant="outlined"
          type="search"
          aria-label={t(placeholderKey || 'search_placeholder') || 'search'}
          placeholder={t(placeholderKey || 'search_placeholder') || ''}
          value={searchQuery}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: showClear && searchQuery ? (
              <InputAdornment position="end">
                <IconButton aria-label="clear search" onClick={handleClear} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ width: '90%', mr: 4 }}
        />

        <Button
          variant="outlined"
          onClick={handleSearchButton}
          sx={{
            width: '10%',
            height: '55px',
            color: '#310AA2',
            fontWeight: 'bold',
            border: '2px solid',
            borderColor: '#310AA2',
          }}
        >
          {loading ? t('loading') : t('search_button')}
        </Button>
      </Box>

      {activeFilters && activeFilters.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {activeFilters.map((f) => (
            <Chip
              key={f.key}
              label={f.label ?? `${f.key}: ${f.value}`}
              onDelete={f.removable ? () => handleRemoveFilter(f.key) : undefined}
              variant={f.removable ? 'outlined' : 'filled'}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default SearchBar
